import { useMemo } from "react";
import { Repeat, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { cn, formatCurrency, parseCurrency } from "../lib/utils";

const FREQUENCIES = [
  { value: "daily", label: "Diária", intervalLabel: "dia(s)" },
  { value: "weekly", label: "Semanal", intervalLabel: "semana(s)" },
  { value: "monthly", label: "Mensal", intervalLabel: "mês(es)" },
  { value: "yearly", label: "Anual", intervalLabel: "ano(s)" },
];

const RECURRENCE_LABELS = {
  none: "Único",
  installment: "Parcelado",
  fixed: "Fixo",
};

function toNumber(value) {
  if (typeof value === "number") return value;
  return parseCurrency(value);
}

function toPositiveInt(value, fallback, min = 1) {
  const parsed = parseInt(value, 10);
  if (Number.isNaN(parsed)) return fallback;
  return Math.max(min, parsed);
}

function RepeatFields({
  frequency,
  setFrequency,
  interval,
  setInterval,
}) {
  const selectedFrequency = FREQUENCIES.find((item) => item.value === frequency) || FREQUENCIES[2];

  return (
    <div className="grid gap-4 sm:grid-cols-[1fr_160px]">
      <div className="space-y-2">
        <Label>Periodicidade</Label>
        <Select value={frequency} onValueChange={setFrequency}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FREQUENCIES.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Repete-se a cada</Label>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min="1"
            max="24"
            value={interval}
            onChange={(event) => setInterval(event.target.value)}
            className="font-mono"
          />
          <span className="w-20 text-sm text-muted-foreground">{selectedFrequency.intervalLabel}</span>
        </div>
      </div>
    </div>
  );
}

export function getRepeatSummary(settings) {
  const recurrenceType = settings?.recurrenceType || "none";
  if (recurrenceType === "none") return "Único";

  const selectedFrequency = FREQUENCIES.find((item) => item.value === settings.recurrenceFrequency) || FREQUENCIES[2];
  const interval = toPositiveInt(settings.recurrenceInterval, 1);

  if (recurrenceType === "installment") {
    const total = toPositiveInt(settings.installmentCount, 2, 2);
    const start = Math.min(toPositiveInt(settings.installmentStartIndex, 1), total);
    return start > 1
      ? `Parcelado: ${start}/${total} em ${selectedFrequency.label.toLowerCase()}`
      : `Parcelado: ${total}x ${selectedFrequency.label.toLowerCase()}`;
  }

  if (settings.fixedTotalEnabled) {
    return `Fixo: ${toPositiveInt(settings.fixedTotalCount, 1)} ocorrência(s)`;
  }

  return interval > 1
    ? `Fixo: a cada ${interval} ${selectedFrequency.intervalLabel}`
    : `Fixo: ${selectedFrequency.label}`;
}

export function RepeatTransactionButton({ settings, onClick, className }) {
  return (
    <Button
      type="button"
      variant="outline"
      className={cn("h-auto w-full justify-between px-3 py-3 text-left font-normal", className)}
      onClick={onClick}
      data-testid="repeat-transaction-button"
    >
      <span className="flex min-w-0 items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Repeat className="h-4 w-4" />
        </span>
        <span className="min-w-0">
          <span className="block text-xs font-medium uppercase tracking-normal text-muted-foreground">Repetir</span>
          <span className="block truncate font-medium">{getRepeatSummary(settings)}</span>
        </span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
    </Button>
  );
}

export function RepeatTransactionDialog({
  open,
  onOpenChange,
  amount,
  recurrenceType,
  setRecurrenceType,
  recurrenceFrequency,
  setRecurrenceFrequency,
  recurrenceInterval,
  setRecurrenceInterval,
  installmentCount,
  setInstallmentCount,
  installmentStartIndex,
  setInstallmentStartIndex,
  amountMode,
  setAmountMode,
  fixedTotalEnabled,
  setFixedTotalEnabled,
  fixedTotalCount,
  setFixedTotalCount,
}) {
  const parsedAmount = toNumber(amount);
  const totalInstallments = toPositiveInt(installmentCount, 2, 2);
  const startIndex = Math.min(toPositiveInt(installmentStartIndex, 1), totalInstallments);

  const installmentValue = useMemo(() => {
    if (amountMode === "total") return parsedAmount / totalInstallments;
    return parsedAmount;
  }, [amountMode, parsedAmount, totalInstallments]);

  const handleTabChange = (value) => {
    setRecurrenceType(value);
  };

  const applyAndClose = () => {
    if (recurrenceType === "installment" && startIndex !== toPositiveInt(installmentStartIndex, 1)) {
      setInstallmentStartIndex(String(startIndex));
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Definir repetição</DialogTitle>
        </DialogHeader>

        <Tabs value={recurrenceType} onValueChange={handleTabChange} className="space-y-5">
          <TabsList className="grid h-auto w-full grid-cols-3">
            <TabsTrigger value="none" className="py-2">Único</TabsTrigger>
            <TabsTrigger value="installment" className="py-2">Parcelado</TabsTrigger>
            <TabsTrigger value="fixed" className="py-2">Fixo</TabsTrigger>
          </TabsList>

          <TabsContent value="none" className="space-y-4">
            <div className="rounded-md border bg-muted/30 p-4">
              <p className="font-medium">{RECURRENCE_LABELS.none}</p>
              <p className="mt-1 text-sm text-muted-foreground">Será criado somente o lançamento atual.</p>
            </div>
          </TabsContent>

          <TabsContent value="installment" className="space-y-4">
            <RepeatFields
              frequency={recurrenceFrequency}
              setFrequency={setRecurrenceFrequency}
              interval={recurrenceInterval}
              setInterval={setRecurrenceInterval}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Número de parcelas</Label>
                <Input
                  type="number"
                  min="2"
                  max="120"
                  value={installmentCount}
                  onChange={(event) => setInstallmentCount(event.target.value)}
                  className="font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label>Parcela inicial</Label>
                <Input
                  type="number"
                  min="1"
                  max={totalInstallments}
                  value={installmentStartIndex}
                  onChange={(event) => setInstallmentStartIndex(event.target.value)}
                  className="font-mono"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Valor da parcela</Label>
              <Input value={formatCurrency(installmentValue)} readOnly className="font-mono" />
            </div>

            <div className="flex items-center justify-between gap-4 rounded-md border p-3">
              <Label htmlFor="amount-mode" className="leading-snug">
                Considerar valor informado como valor da parcela
              </Label>
              <Switch
                id="amount-mode"
                checked={amountMode === "per_installment"}
                onCheckedChange={(checked) => setAmountMode(checked ? "per_installment" : "total")}
              />
            </div>
          </TabsContent>

          <TabsContent value="fixed" className="space-y-4">
            <RepeatFields
              frequency={recurrenceFrequency}
              setFrequency={setRecurrenceFrequency}
              interval={recurrenceInterval}
              setInterval={setRecurrenceInterval}
            />

            <div className="flex items-center justify-between gap-4 rounded-md border p-3">
              <Label htmlFor="fixed-total" className="leading-snug">
                Definir total de ocorrências
              </Label>
              <Switch id="fixed-total" checked={fixedTotalEnabled} onCheckedChange={setFixedTotalEnabled} />
            </div>

            {fixedTotalEnabled && (
              <div className="space-y-2">
                <Label>Total de ocorrências</Label>
                <Input
                  type="number"
                  min="1"
                  max="120"
                  value={fixedTotalCount}
                  onChange={(event) => setFixedTotalCount(event.target.value)}
                  className="font-mono"
                />
              </div>
            )}
          </TabsContent>
        </Tabs>

        <div className="flex gap-2 pt-2">
          <Button type="button" variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="button" className="flex-1 btn-glow" onClick={applyAndClose}>
            Aplicar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
